import { environment } from '../../environments/environment';

/**
 * Backend legal.model.LegalPlatform: the app a document belongs to. GENERAL is the part every
 * product shares - no app is GENERAL itself, see {@link legalPartsOf}.
 */
export type LegalPlatform = 'DENTAL_LINK' | 'ACCOUNTING_LINK' | 'LAB' | 'DENTIST' | 'COURIER' | 'GENERAL';

export const LEGAL_PLATFORM_LABELS: Record<LegalPlatform, string> = {
  DENTAL_LINK: 'Dental Link',
  ACCOUNTING_LINK: 'Accounting Link',
  LAB: 'Dental Link Lab',
  DENTIST: 'Dental Link Dentist',
  COURIER: 'Courier Link',
  GENERAL: 'General (all products)'
};

export const LEGAL_PLATFORM_ICONS: Record<LegalPlatform, string> = {
  DENTAL_LINK: 'medical_services',
  ACCOUNTING_LINK: 'account_balance',
  LAB: 'biotech',
  DENTIST: 'medical_services',
  COURIER: 'local_shipping',
  GENERAL: 'gavel'
};

/**
 * The apps whose documents this hub's API holds, first one selected by default. The running system
 * has the combined Dental Link app and Accounting Link; the new stack the lab, dentist and courier
 * builds instead of the combined app. Set per environment.
 */
export const LEGAL_PLATFORMS: LegalPlatform[] = environment.legalPlatforms as LegalPlatform[];

/** Backend legal.model.LegalDocumentType. The DPA is accepted by each organisation's owner, for the organisation. */
export type LegalDocumentType = 'TERMS_OF_USE' | 'PRIVACY_POLICY' | 'DATA_PROCESSING_AGREEMENT';

/** Backend legal.model.LegalDocumentStatus. A published version is frozen. */
export type LegalDocumentStatus = 'DRAFT' | 'PUBLISHED';

export const LEGAL_DOCUMENT_TYPE_LABELS: Record<LegalDocumentType, string> = {
  TERMS_OF_USE: 'Terms of Use',
  PRIVACY_POLICY: 'Privacy Policy',
  DATA_PROCESSING_AGREEMENT: 'Data Processing Agreement'
};

export const LEGAL_DOCUMENT_TYPES: LegalDocumentType[] = ['TERMS_OF_USE', 'PRIVACY_POLICY', 'DATA_PROCESSING_AGREEMENT'];

/**
 * Backend LegalPlatform.parts: the documents, in reading order, that make up an app's document. A
 * product's Terms of Use and DPA are the General part and its addendum; its Privacy Policy is the
 * General one. The old stack's apps keep their own documents.
 */
export function legalPartsOf(platform: LegalPlatform, type: LegalDocumentType): LegalPlatform[] {
  switch (platform) {
    case 'LAB':
    case 'DENTIST':
    case 'COURIER':
      return type === 'PRIVACY_POLICY' ? ['GENERAL'] : ['GENERAL', platform];
    default:
      return [platform];
  }
}

/** The document types a platform writes itself. The old stack's apps have no DPA. */
export function legalTypesOf(platform: LegalPlatform): LegalDocumentType[] {
  return LEGAL_DOCUMENT_TYPES.filter(type => legalPartsOf(platform, type).includes(platform)
    && (type !== 'DATA_PROCESSING_AGREEMENT' || platform !== 'DENTAL_LINK' && platform !== 'ACCOUNTING_LINK'));
}

export function isOrganisational(type: LegalDocumentType): boolean {
  return type === 'DATA_PROCESSING_AGREEMENT';
}

/** Backend LegalComposedDocumentDTO: an app's document as its users read it, part by part. */
export interface ComposedLegalDocument {
  platform: LegalPlatform;
  type: LegalDocumentType;
  organisational: boolean;
  parts: {
    id: number;
    platform: LegalPlatform;
    type: LegalDocumentType;
    /** Null on a draft, which the preview shows in place of the current version. */
    version: number | null;
    title: string;
    content: string;
  }[];
}

/** Backend LegalDocumentDTO. */
export interface LegalDocument {
  id: number;
  platform: LegalPlatform;
  type: LegalDocumentType;
  /** Assigned at publish; null on a draft. */
  version: number | null;
  title: string;
  /** Markdown. */
  content: string;
  changeSummary: string | null;
  status: LegalDocumentStatus;
  /** The newest published version of its platform and type: the one every user of that app must have accepted. */
  current: boolean;
  publishedAt: string | null;
  publishedBy: string | null;
  updatedAt: string | null;
  acceptedCount: number;
  /**
   * Only on the current version. Counts the members whose role can use the document's app - for a
   * DPA, the organisations that have not accepted.
   */
  pendingCount: number | null;
}

export interface LegalDocumentCreateRequest {
  platform: LegalPlatform;
  type: LegalDocumentType;
  title: string;
  content: string;
  changeSummary: string | null;
}

export interface LegalDocumentEditRequest {
  title: string;
  content: string;
  changeSummary: string | null;
}

/** Backend LegalAcceptanceDTO. Name and email come from Keycloak and are null for a deleted account. */
export interface LegalAcceptance {
  userId: string;
  name: string | null;
  email: string | null;
  tenantId: number | null;
  tenantName: string | null;
  acceptedAt: string;
  ipAddress: string | null;
  userAgent: string | null;
}

/** Backend LegalPendingUserDTO. */
export interface LegalPendingUser {
  userId: string;
  name: string | null;
  email: string | null;
  tenantNames: string[];
}
